import {
  Hidden, Box, Stack, useMediaQuery
} from '@mui/material'
import { styled } from '@mui/system'
import { Image, Tag } from 'bloomer'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useTheme } from '@mui/material/styles'
import { IFolder } from '../../../../api/FolderApi'
import checksIcon from '../../../../images/menu-icons/checks.svg'
import { FolderChecklist } from '../../../../types/FolderChecklist'
import DateUtil from '../../../../util/DateUtil'
import { TableDueDate } from '../TableDueDate'

interface Props {
  data: FolderChecklist,
  checklistStatus: 'open' | 'closed' | 'due' | 'invited',
  tabIndex: number,
  folder: IFolder,
  offlineIds: string[]
}

const TitleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '100%'
    // no overflow:hidden — we want text to wrap, not clip
  },
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    maxWidth: '441px'
  }
}))

export default function TitleColumn({
  data,
  folder,
  tabIndex,
  checklistStatus,
  offlineIds
} : Props) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const getLinkToChecklist = (x: FolderChecklist): string => {
    if (checklistStatus === 'closed') {
      return `/checklists/${x.checklist_id}/audit/${x.id}/report`
    }
    return `/checklists/tab/${tabIndex}/folders/${folder.id}/${x.checklist_id}/audit/${x.id}`
  }

  return (
    <Box
      component={Link}
      to={getLinkToChecklist(data)}
      sx={{
        display: { xs: 'flex', md: 'block' },
        flexWrap: 'wrap',
        minWidth: 0,
        width: '100%'
      }}
    >
      <TitleWrapper>
        <Image
          className="has-margin-right-0"
          isSize="24x24"
          src={checksIcon}
          style={{ flexShrink: 0, marginTop: '2px' }}
        />
        {data.is_audit && (
        <Tag
          className="audit-tag"
          data-tip={t('questions.auditPoints')}
          isColor="link"
        >
          {data.audit_points}
        </Tag>
        )}
        {/* Mobile: wrap naturally. Desktop: single line with ellipsis. */}
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            // xs = phone cards: let text wrap freely
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            overflow: { xs: 'visible', md: 'hidden' },
            textOverflow: { xs: 'unset', md: 'ellipsis' },
            wordBreak: { xs: 'break-word', md: 'normal' }
          }}
        >
          {data.title}
        </Box>
        {offlineIds.includes(data.id) && (
        <span className="offline-icon icon">
          <i className="fas fa-download" />
        </span>
        )}
      </TitleWrapper>
      <Hidden implementation="js" lgUp>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 0.75, ml: { xs: 0, md: 1 }, flexWrap: 'wrap', gap: '4px' }}
        >
          {data.due_date && (
          <TableDueDate
            date={DateUtil.parse(data.due_date)}
          />
          )}
          {data.created_at && (
          <Tag className="is-rounded is-light created-at-tag">
            {DateUtil.format(data.created_at, isPhone ? DateUtil.ShortFormat : DateUtil.LongFormat)}
          </Tag>
          )}
        </Stack>
      </Hidden>
    </Box>
  )
}
